/*
 * marking-labels/input.js — коды из того, что у продавца на руках.
 *
 * ГЛАВНЫЙ ВХОД — PDF ИЗ «ЧЕСТНОГО ЗНАКА». СУЗ отдаёт коды только PDF или EPS:
 * CSV с полными кодами отключён (полный код — только системам по API), а CSV
 * повторной печати — это КИ без кода проверки, по нему DataMatrix не
 * напечатать. Значит, полный код живёт только в КАРТИНКЕ DataMatrix на
 * странице PDF. Текстовый слой несёт максимум КИ (01+GTIN+21+серийный).
 *
 * Поэтому каждая страница рисуется (pdf.js) и читается ZXing'ом
 * (window.__qrDecode) — все DataMatrix страницы, а текстовый слой служит
 * СВЕРКОЙ: каждый КИ, напечатанный текстом, обязан найтись среди прочитанных
 * кодов. Не нашёлся — значит, код на странице есть, а прочитать его не
 * вышло, и человек узнаёт об этом с номером страницы, а не на кассе.
 *
 * Термо-шаблон ЧЗ — одна этикетка 6,0×3,5 см на страницу, рисуется при
 * 400 dpi (≈11 px на модуль при модуле 0,7 мм); лист A4 — при 250 dpi и,
 * если КИ на странице больше, чем прочитано кодов, ещё раз окнами
 * (DataMatrix ≤ 25 мм в окне ~1100 px).
 *
 * Остальные входы: CSV/TXT/TSV (marking-labels/codes.js → extract), XLSX
 * (SheetJS по нажатию), картинки (фото/скан этикеток — лестница __qrDecode),
 * EPS — честная попытка найти коды в тексте файла.
 *
 * window.MarkingInput.read(file, {onProgress, signal}) → Promise<{
 *    kind: 'pdf'|'text'|'xlsx'|'image'|'eps',
 *    items: [parsed code + {source, page, line, symbology}],
 *    pages, missed: [{page, ki}], notGs1: n, skipped, badLines, note }>
 */
(function () {
    'use strict';
    if (window.MarkingInput) return;
    var MC = window.MarkingCodes;

    var CFG = function () { return window.__MARKING || {}; };

    function aborted(signal) {
        if (signal && signal.aborted) { var e = new Error('aborted'); e.name = 'AbortError'; throw e; }
    }

    function sniff(file) {
        var name = String(file && file.name || '').toLowerCase();
        return file.slice(0, 12).arrayBuffer().then(function (ab) {
            var b = new Uint8Array(ab);
            var head = String.fromCharCode.apply(null, b);
            if (head.indexOf('%PDF') === 0) return 'pdf';
            if (b[0] === 0xC5 && b[1] === 0xD0 && b[2] === 0xD3 && b[3] === 0xC6) return 'eps';   // DOS EPS
            if (head.indexOf('%!PS') === 0) return 'eps';
            if (b[0] === 0x50 && b[1] === 0x4B) return 'xlsx';                                   // zip
            if (b[0] === 0xD0 && b[1] === 0xCF) return 'xls';
            // «ftypheic» / «ftypmif1» — HEIC с iPhone, даже без расширения в имени.
            if ((b[0] === 0x89 && b[1] === 0x50) || (b[0] === 0xFF && b[1] === 0xD8) || head.indexOf('RIFF') === 0 || /^ftyp(hei|hev|mif1|msf1|avif)/.test(head.slice(4)) || /\.(png|jpe?g|webp|heic|heif|bmp|gif)$/.test(name)) return 'image';
            return 'text';
        });
    }

    function bytes(file) {
        return (window.FileBytes && typeof window.FileBytes.arrayBuffer === 'function')
            ? window.FileBytes.arrayBuffer(file) : file.arrayBuffer();
    }

    /* Текст файла: UTF-8, иначе windows-1251 (русский Excel) — тем же
       распознавателем кодировки, что у /label-maker (subtitle-parser.js). */
    function textOf(file) {
        return bytes(file).then(function (ab) {
            var u8 = new Uint8Array(ab);
            var SP = window.SubtitleParser;
            if (SP && typeof SP.decodeBytes === 'function') {
                try { var r = SP.decodeBytes(ab); if (r && typeof r.text === 'string') return r.text; } catch (_) {}
            }
            try { return new TextDecoder('utf-8', { fatal: true }).decode(u8); }
            catch (_) { return new TextDecoder('windows-1251').decode(u8); }
        });
    }

    /* ── PDF ──────────────────────────────────────────────────────────── */
    /* Через дверь layout'а (window.__pdfjs): она дождётся загрузчика, если тот
       опоздал, и откажет его же контрактом (PdfjsGoesThroughOneDoorTest). */
    function loadPdfjs() {
        if (typeof window.__pdfjs !== 'function') return Promise.reject(new Error('pdfjs_load_fail'));
        return window.__pdfjs(CFG().pdfjs || [{ mjs: '/vendor/pdfjs/4/pdf.min.mjs', worker: '/vendor/pdfjs/4/pdf.worker.min.mjs' }]);
    }

    /* КИ из текстового слоя страницы: одной строкой («(01)…(21)…»,
       «0104…215…»), двумя («01…» и под ней «21…») — и с ПЕРЕНОСОМ ВНУТРИ
       серийного номера. Так печатает сам СУЗ (настоящие PDF, 23.10.2026:
       термо 35×61 мм, «0104620519012385215Hn» / «>RQaXrnQROq?Kv5)m» — строка
       кончилась на третьем знаке серийного). Поэтому у КИ — варианты
       серийного (`alts`): строка как есть и она же с одной-двумя следующими
       строками; сверка принимает любой (kiFound). До правки каждая страница
       такого файла называлась «DataMatrix не прочитался», хотя прочитаны были
       все. Строка, кончившаяся сразу после «21» («(01)…(21)» / серийный ниже),
       — КИ «слабый»: его считают (решение про второй проход окнами), но
       пропажей не называют — чем продолжена строка, по тексту не узнать. */
    var KI_HEAD = /(?:\(01\)|01)(\d{14})(?:\(21\)|21)(.*)$/;
    var KI_START = /^(?:\(01\)|01)\d{14}/;
    function cutAi(s) { var c = /\(\d{2,4}\)/.exec(s); return c ? s.slice(0, c.index) : s; }
    function kisFromText(items) {
        var strs = items.map(function (it) { return String(it.str || '').replace(/\s+/g, ''); }).filter(Boolean);
        var out = [];
        for (var i = 0; i < strs.length; i++) {
            var s = strs[i], gtin = null, ser = '', next = i + 1;
            var m = KI_HEAD.exec(s);
            if (m) { gtin = m[1]; ser = m[2]; }
            else {
                var g = /^\(?01\)?(\d{14})$/.exec(s), n = strs[i + 1] && /^\(?21\)?(.+)$/.exec(strs[i + 1]);
                if (g && n) { gtin = g[1]; ser = n[1]; s = strs[i + 1]; next = i + 2; }
            }
            if (gtin == null) continue;
            // «(21)…(240)…» — серийный до следующей скобки AI.
            var bracketed = /\(\d{2}\)/.test(s);
            var stop = bracketed && /\(\d{2,4}\)/.test(ser);
            if (bracketed) ser = cutAi(ser);
            var alts = ser ? [ser] : [], acc = ser;
            for (var j = next; !stop && j < strs.length && j < next + 2; j++) {
                var t = strs[j];
                // Следующий код, строка-GTIN под этикеткой или следующий AI — не продолжение.
                if (KI_START.test(t) || /^\d{13,14}$/.test(t) || /^\(\d{2,4}\)/.test(t)) break;
                acc += bracketed ? cutAi(t) : t;
                alts.push(acc);
                if (bracketed && /\(\d{2,4}\)/.test(t)) break;
            }
            if (!alts.length) continue;
            out.push({ gtin: gtin, serial: alts[0], alts: alts, weak: !ser });
            i = next - 1;
        }
        return out;
    }

    /* КИ, напечатанный текстом, нашёлся среди прочитанных кодов страницы:
       серийный совпал с одним из вариантов или начинается с него (≥ 3 знаков —
       перенос строки мог оставить на первой строке три знака). */
    function kiFound(k, items, pageNo) {
        return items.some(function (it) {
            if (it.page !== pageNo || it.gtin !== k.gtin || it.serial == null) return false;
            var alts = k.alts || [k.serial];
            for (var a = 0; a < alts.length; a++) {
                if (it.serial === alts[a] || (alts[a].length >= 3 && it.serial.indexOf(alts[a]) === 0)) return true;
            }
            return false;
        });
    }

    function renderPage(page, dpi, region, canvas) {
        var vp = page.getViewport({ scale: dpi / 72 });
        var r = region || { x: 0, y: 0, w: vp.width, h: vp.height };
        canvas.width = Math.max(1, Math.round(r.w));
        canvas.height = Math.max(1, Math.round(r.h));
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        return page.render({
            canvasContext: ctx, viewport: vp,
            transform: [1, 0, 0, 1, -r.x, -r.y],
            background: '#ffffff',
        }).promise.then(function () {
            return ctx.getImageData(0, 0, canvas.width, canvas.height);
        });
    }

    var DM_ONLY = ['DATA_MATRIX'];

    function decodeImage(id) {
        return window.__qrDecode.fromImageData(id, { tiling: false, maxSymbols: 128, formats: DM_ONLY });
    }

    function readPdf(file, opts) {
        opts = opts || {};
        var src = file.name || 'PDF';
        return Promise.all([loadPdfjs(), bytes(file), window.__qrDecode.ready()]).then(function (r) {
            var pdfjs = r[0];
            return pdfjs.getDocument({ data: new Uint8Array(r[1]), isEvalSupported: false }).promise;
        }).then(function (doc) {
            var canvas = document.createElement('canvas');
            var items = [], missed = [], notGs1 = 0, pages = doc.numPages, kisTotal = 0;
            var n = 0;
            function next() {
                aborted(opts.signal);
                if (n >= pages) {
                    canvas.width = canvas.height = 0;
                    try { doc.destroy(); } catch (_) {}
                    return { kind: 'pdf', items: items, pages: pages, missed: missed, notGs1: notGs1, kis: kisTotal };
                }
                n++;
                var pageNo = n;
                return doc.getPage(pageNo).then(function (page) {
                    var vp1 = page.getViewport({ scale: 1 });
                    var areaMm2 = (vp1.width * 25.4 / 72) * (vp1.height * 25.4 / 72);
                    var small = areaMm2 < 160 * 110;            // этикетка, а не лист
                    var dpi = small ? 400 : 250;
                    return page.getTextContent().then(function (tc) {
                        var kis = kisFromText(tc.items || []);
                        kisTotal += kis.length;
                        return renderPage(page, dpi, null, canvas).then(decodeImage).then(function (res) {
                            // Меньше прочитано, чем КИ напечатано текстом, — второй проход окнами.
                            if (small || res.length >= kis.length) return res;
                            return tiles(page, dpi * 1.6, canvas, res);
                        }).then(function (res) {
                            var seen = {};
                            res.forEach(function (hit) {
                                if (seen[hit.text]) return;
                                seen[hit.text] = 1;
                                var p = MC.parse(hit.text);
                                p.source = src; p.page = pageNo; p.symbology = hit.symbology || null;
                                if (hit.symbology && hit.symbology !== ']d2') {
                                    // DataMatrix БЕЗ FNC1 — частая ошибка печати; код в такой форме касса не примет.
                                    p.issues.push({ code: 'not_gs1_source', level: 'info' });
                                    notGs1++;
                                }
                                items.push(p);
                            });
                            kis.forEach(function (k) {
                                if (k.weak || kiFound(k, items, pageNo)) return;
                                missed.push({ page: pageNo, ki: '01' + k.gtin + '21' + k.serial, gtin: k.gtin, serial: k.serial });
                            });
                            try { page.cleanup(); } catch (_) {}
                            if (opts.onProgress) opts.onProgress(pageNo, pages);
                        });
                    });
                }).then(next);
            }
            return next();
        });
    }

    /* Второй проход: окна с перекрытием в треть, каждое — при повышенном dpi.
       Перекрытие больше самого крупного кода на листе (≤ 25 мм), так что
       любой код целиком попадает хотя бы в одно окно. */
    function tiles(page, dpi, canvas, first) {
        var vp = page.getViewport({ scale: dpi / 72 });
        var tileW = 1400, tileH = 1400;
        var stepX = tileW * 0.66, stepY = tileH * 0.66;
        var regions = [];
        for (var y = 0; y < vp.height; y += stepY) {
            for (var x = 0; x < vp.width; x += stepX) {
                regions.push({ x: x, y: y, w: Math.min(tileW, vp.width - x), h: Math.min(tileH, vp.height - y) });
                if (x + tileW >= vp.width) break;
            }
            if (y + tileH >= vp.height) break;
        }
        var all = first.slice();
        var i = 0;
        function step() {
            if (i >= regions.length) return all;
            var rg = regions[i++];
            return renderPage(page, dpi, rg, canvas).then(decodeImage).then(function (res) {
                all = all.concat(res);
                return step();
            });
        }
        return step();
    }

    /* ── картинка: фото или скан этикеток ─────────────────────────────── */
    /* Фото с iPhone приходит HEIC: браузеры, кроме Safari, его не открывают —
       общий декодер (HeicDecode, его грузит layout) переводит в JPEG. Не вышло —
       отдаём файл как есть: Safari прочтёт и HEIC. */
    function readImage(file) {
        var H = window.HeicDecode;
        var prep = (H && H.isHeic && H.isHeic(file) && H.normalize)
            ? H.normalize(file).catch(function () { return file; })
            : Promise.resolve(file);
        return prep.then(function (f) { return window.__qrDecode.fromFile(f, { maxSymbols: 128, formats: DM_ONLY }); }).then(function (res) {
            var seen = {}, items = [];
            res.forEach(function (hit) {
                if (seen[hit.text]) return;
                seen[hit.text] = 1;
                var p = MC.parse(hit.text);
                p.source = file.name || 'image'; p.symbology = hit.symbology || null;
                items.push(p);
            });
            return { kind: 'image', items: items, pages: 1, missed: [] };
        });
    }

    /* ── таблица Excel ────────────────────────────────────────────────── */
    function loadSheetJs() {
        if (window.XLSX) return Promise.resolve(window.XLSX);
        var chain = CFG().sheetjs || ['/vendor/sheetjs/xlsx.full.min.js'];
        if (typeof window.__vendorScript !== 'function') return Promise.reject(new Error('vendor_loader_missing'));
        return window.__vendorScript(chain, { expect: 'XLSX', label: 'sheetjs' }).then(function () {
            if (!window.XLSX) throw new Error('sheetjs_absent');
            return window.XLSX;
        });
    }

    /* Все листы книги → строки, ячейки через табуляцию (в коде табуляции не
       бывает), дальше — тот же разбор, что у CSV. raw:false — числа так, как
       их видит человек: GTIN-ячейка «4601234567893» без экспоненты. */
    function xlsxText(ab) {
        return loadSheetJs().then(function (X) {
            var wb = X.read(new Uint8Array(ab), { type: 'array', cellText: true, cellDates: false });
            var lines = [];
            wb.SheetNames.forEach(function (name) {
                var rows = X.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, defval: '' });
                rows.forEach(function (r) { lines.push(r.map(function (v) { return String(v == null ? '' : v); }).join('\t')); });
            });
            return lines.join('\n');
        });
    }

    function readXlsx(file) {
        return bytes(file).then(xlsxText).then(function (text) {
            var r = MC.extract(text, file.name || 'xlsx');
            r.kind = 'xlsx'; r.missed = []; r.pages = 0;
            return r;
        });
    }

    function readText(file) {
        return textOf(file).then(function (text) {
            var r = MC.extract(text, file.name || 'text');
            r.kind = 'text'; r.missed = []; r.pages = 0;
            return r;
        });
    }

    /* EPS из СУЗ: устройства файла мы не видели. Если коды лежат в нём
       текстом (строки PostScript), они находятся; если только картинкой — честный
       отказ с советом выгрузить PDF. */
    function readEps(file) {
        return bytes(file).then(function (ab) {
            var u8 = new Uint8Array(ab), s = '';
            for (var i = 0; i < u8.length; i += 65536) s += String.fromCharCode.apply(null, u8.subarray(i, i + 65536));
            s = s.replace(/\\([()\\])/g, '$1').replace(/\\035/g, '\x1d');
            var found = [], re = /\(?01\)?\d{14}\(?21\)?[\x1d\x21-\x7e]{6,120}/g, m, seen = {};
            while ((m = re.exec(s))) {
                var cand = m[0].replace(/[)\]>]+$/, '');
                var p = MC.parse(cand);
                if (p.ok && !seen[p.code]) { seen[p.code] = 1; p.source = file.name || 'eps'; found.push(p); }
            }
            return { kind: 'eps', items: found, pages: 0, missed: [], note: found.length ? 'eps_text' : 'eps_unreadable' };
        });
    }

    function read(file, opts) {
        return sniff(file).then(function (kind) {
            if (kind === 'pdf') return readPdf(file, opts);
            if (kind === 'image') return readImage(file);
            if (kind === 'xlsx') return readXlsx(file);
            // Excel 97–2003 (.xls) SheetJS читает сам — сервер не нужен.
            if (kind === 'xls') return readXlsx(file);
            if (kind === 'eps') return readEps(file);
            return readText(file);
        });
    }

    window.MarkingInput = {
        read: read,
        readPdf: readPdf,
        kisFromText: kisFromText,
        kiFound: kiFound,
        xlsxText: xlsxText,
        loadSheetJs: loadSheetJs,
        textOf: textOf,
    };
})();
